import { Component, OnDestroy, OnInit, viewChild } from '@angular/core';
import { ApolloService } from '@src/app/services/apollo/apollo.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { PortalConfiguration } from '@src/app/model/portal-configuration';
import { filter, first, map, switchMap, take, tap } from 'rxjs/operators';
import { Retailer } from '@src/app/model/retailer';
import { ScopeService } from '@src/app/services/scope/scope.service';
import { Observable, Subject, takeUntil } from 'rxjs';
import { RetailerScope } from '@src/app/model/retailer-scope';
import { ScopeSelectionComponent } from '@src/app/components/scope-selection/scope-selection.component';
import { Id } from '@ess/jg-rule-executor';
import { PublicRetailer } from '@src/app/model/public-retailer';
import { TranslateService } from '@ngx-translate/core';
import { validateNoHtmlInjection } from '@src/app/validators/validators';
import { AssetService } from '@src/app/services/asset/asset.service';
import { PayloadService } from '@src/app/services/payload/payload.service';
import { ConfirmationModalComponent } from '@src/app/components/modals/confirmation-modal/confirmation-modal.component';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'app-configuration',
  templateUrl: './configuration.component.html',
  styleUrls: ['./configuration.component.scss'],
  standalone: false,
})
export class ConfigurationComponent implements OnInit, OnDestroy {
  private _componentDestroyed$: Subject<void> = new Subject();

  waitingForResponse: boolean = false;
  public submittedGetAtLeastOnce: boolean = false;
  public submittedUpdateAtLeastOnce: boolean = false;
  public maxLength: number = 32;
  public minLength: number = 1;

  public availableScopes: PublicRetailer[];
  public readonly selectedScope$: Observable<RetailerScope>;

  // required by the value binding of the mat form
  public selectedScopeId: Id<Retailer>;

  public configuration: PortalConfiguration;

  readonly scopeSelection = viewChild(ScopeSelectionComponent);

  public getConfigForm: FormGroup;
  public updateConfigForm: FormGroup;

  public payloadBusy: boolean = false;
  private _lastPayloadContent: string;

  private _assetFailMsg: string = 'configuration.uploadAsset.fail';
  private _assetSuccessMsg: string = 'configuration.uploadAsset.success';

  private _failMsg: string = 'configuration.modal.fail';

  public allChecked: boolean = true; // all scopes are checked by default

  constructor(
    private _formBuilder: FormBuilder,
    private _scopeService: ScopeService,
    private _apolloService: ApolloService,
    private _translateService: TranslateService,
    private _assetService: AssetService,
    private _payloadService: PayloadService,
    private _dialog: MatDialog
  ) {
    this.getConfigForm = this._formBuilder.group({
      code: ['', [Validators.required, Validators.maxLength(this.maxLength), Validators.minLength(this.minLength)]],
    });
    this.updateConfigForm = this._formBuilder.group({
      name: ['', [validateNoHtmlInjection, Validators.maxLength(this.maxLength), Validators.minLength(this.minLength)]],
      newCode: ['', [Validators.maxLength(this.maxLength), Validators.minLength(this.minLength)]],
    });
    this.selectedScope$ = this._scopeService.currentScope$
      .pipe(takeUntil(this._componentDestroyed$), filter(s => !!s));

    this.selectedScope$
      .pipe(takeUntil(this._componentDestroyed$))
      .subscribe(s => this.selectedScopeId = s?.id);
  }

  public ngOnInit(): void {
    this._getAllowedScopes();
  }

  // convenience getter for easy access to form fields
  get f1(): FormGroup['controls'] {
    return this.getConfigForm.controls;
  }

  get f2(): FormGroup['controls'] {
    return this.updateConfigForm.controls;
  }

  onSubmitGetConfig(): void {
    this.f1.code.updateValueAndValidity();
    this.f1.code.updateValueAndValidity(); // removes 'wrong' error from previous failed attempt

    this.submittedGetAtLeastOnce = true;
    this.waitingForResponse = true;

    if (this.f1.code.valid) {
      this._apolloService.getConfigurationByCode$(this.f1.code.value)
        .pipe(take(1))
        .subscribe((r) => {
          if (!!r) {
            this._lastPayloadContent = undefined;
            this.configuration = r;
          } else {
            window.alert('Configuration not found');
          }
        });
    }

    this.waitingForResponse = false;
  }

  /**
   * Turns the current config into a pre-config if it isn't one, and vice versa, and updates the code and/or name if a new one was given
   */
  onSubmitUpdateConfig(togglePreConfig: boolean = false): void {
    this.f2.newCode.updateValueAndValidity();
    this.f2.newCode.updateValueAndValidity(); // removes 'wrong' error from previous failed attempt
    this.f2.name.updateValueAndValidity();
    this.f2.name.updateValueAndValidity(); // removes 'wrong' error from previous failed attempt

    this.submittedUpdateAtLeastOnce = true;
    this.waitingForResponse = true;

    if (this.f2.newCode.valid && this.configuration) {
      const newCode = this.f2.newCode.value.trim() !== '' ? this.f2.newCode.value : undefined;
      const newName = this.f2.name.value.trim() !== '' ? this.f2.name.value : undefined;
      const toPreConfig = togglePreConfig
        ? !this.configuration.isPreConfiguration
        : this.configuration.isPreConfiguration;

      if (newCode) {
        this._apolloService.getConfigurationByCode$(newCode).subscribe(
          (res) => {
            if (res) {
              this._openConfirmationModal(
                'configuration.confirm.header',
                'configuration.confirm.description',
                this._updateAndGetConfig$(this.configuration.code, toPreConfig, false, [], newCode, newName),
                () => this._onNextConfigurationUpdate(true, togglePreConfig, [])
              );
            } else {
              this._updateConfiguration([], togglePreConfig, toPreConfig, false, newCode, newName);
            }
          }
        );
      } else {
        this._updateConfiguration([], togglePreConfig, toPreConfig, false, newCode, newName);
      }
    }

    this.waitingForResponse = false;
  }

  forceConfigurationPriceRefresh(): void {
    this.waitingForResponse = true;

    if (this.configuration) {
      this._updateConfiguration([], false, this.configuration.isPreConfiguration, true);
    }

    this.waitingForResponse = false;
  }

  private _openConfirmationModal(
    header: string,
    content: string,
    updater: Observable<boolean>,
    onSuccess: () => void
  ): void {
    const dialogRef = this._dialog.open(ConfirmationModalComponent,
      {
        data: {
          headerText: header,
          contentText: content,
        },
      }
    );
    ConfirmationModalComponent.useConfirmDialog(
      dialogRef,
      updater,
      this._translateService.instant(this._failMsg),
      onSuccess
    );
  }

  /**
   * Copy the configuration to one or more other scopes
   */
  public copyToScopes(): void {
    const scopeStrings = this.scopeSelection().selectedScopes().map(retailer => retailer.id.toString());
    this._updateConfiguration(scopeStrings, false, this.configuration.isPreConfiguration);
  }

  public async getPayload(): Promise<void> {
    // determine scope when request is sent; just in case it takes long and scope somehow is changed in between!
    const localScope: string = this._scopeService.currentScope$.value.id.toString();
    if (!this._lastPayloadContent) {
      try {
        this.payloadBusy = true;
        const newPayloadStr = await this._payloadService.getPayloadString(this.configuration, localScope);
        this.payloadBusy = false;
        this._lastPayloadContent = newPayloadStr;
        this._downloadLastPayload();
      } catch (e) {
        console.log(e);
        this.payloadBusy = false;
      }
    } else {
      this._downloadLastPayload();
    }
  }

  private _downloadLastPayload(): void {
    const blob = new Blob([this._lastPayloadContent], { type: 'application/json' });
    window.open(window.URL.createObjectURL(blob));
  }

  /**
   * Updates the current configuration
   */
  private _updateConfiguration(
    scopes: string[],
    togglePreConfig: boolean,
    toPreConfig: boolean,
    priceRefresh: boolean = false,
    newCode?: string,
    name?: string
  ): void {
    if (this.configuration) {
      this.waitingForResponse = true;
      this._updateAndGetConfig$(this.configuration.code, toPreConfig, priceRefresh, scopes, newCode, name).subscribe({
        next: (r) => {
          this._onNextConfigurationUpdate(r, togglePreConfig, scopes);
        },
        complete: () => this.waitingForResponse = false,
      });
    }
  }

  private _updateAndGetConfig$(
    code: string,
    setToPreConfiguration: boolean,
    forcePriceRefresh: boolean = false,
    scopes: string[],
    newCode: string,
    name: string
  ): Observable<boolean> {
    return this._apolloService.updateConfiguration$(
      code,
      setToPreConfiguration,
      forcePriceRefresh,
      scopes,
      newCode,
      name
    ).pipe(
      switchMap(u => this._apolloService.getConfigurationByCode$(newCode ?? this.configuration.code)
        .pipe(
          map(config => [config, u])
        ) as Observable<[PortalConfiguration, boolean]>
      ),
      map(([config, update]) => {
        if (config) {
          this.configuration = config;
        }
        return update;
      })
    );
  }

  private _onNextConfigurationUpdate(r: boolean, togglePreConfig: boolean, scopes: string[]): void {
    if (!r) {
      window.alert(this._translateService.instant('configuration.submitError'));
    } else {
      if (togglePreConfig) {
        window.alert(
          this._translateService.instant(
            'configuration.submitSuccessPreConfig',
            {
              code: this.configuration.code,
              type: this._translateService.instant(this.configuration.isPreConfiguration
                ? 'configuration.preConfig'
                : 'configuration.normalConfig'),
            }
          )
        );
      } else if (scopes.length === 0) {
        window.alert(
          this._translateService.instant(
            'configuration.submitSuccessUpdate',
            { code: this.configuration.code }
          )
        );
      } else {
        window.alert(
          this._translateService.instant(
            'configuration.submitSuccessCopy',
            { code: this.configuration.code, scopes: scopes.join(', ') }
          )
        );
      }
    }
  }

  public assetUpload(event: any): void {
    const scope = this._scopeService.currentScope$.getValue()?.id;
    if (event.target.files.length > 0 && scope) {
      const assetMap = new Map().set('configuration', this.configuration.id);
      this._assetService.uploadAsset$(event.target.files[0], assetMap)
        .pipe(first())
        .subscribe({
          next: (url) => {
            if (!url) {
              window.alert(this._translateService.instant(this._assetFailMsg));
            } else {
              this._apolloService.getConfigurationByCode$(this.configuration.code).pipe(
                tap(c => this.configuration = c),
                takeUntil(this._componentDestroyed$)
              );
              window.alert(this._translateService.instant(this._assetSuccessMsg));
            }
          },
          error: (_) => {
            window.alert(this._translateService.instant(this._assetFailMsg));
          },
        });
    }
  }

  public setAll(check: boolean): void {
    this.allChecked = check;
    this.scopeSelection().setAll(check);
  }

  private _getAllowedScopes(): void {
    this._scopeService.scopes$
      .pipe(takeUntil(this._componentDestroyed$))
      .subscribe((scopes) => {
        this.availableScopes = scopes.map(s => new PublicRetailer(s.id, s.name));
      });
  }

  public ngOnDestroy(): void {
    this._componentDestroyed$.next();
    this._componentDestroyed$.complete();
  }
}
